import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { AssignmentConfiguration, ValidationError, SubtaskConfiguration } from '../../types'
import { assignmentService } from '../../services/assignmentService'

interface AssignmentsState {
  assignmentData: AssignmentConfiguration | null
  validationErrors: ValidationError[]
  isDirty: boolean
  saving: boolean
  error: string | null
}

const initialState: AssignmentsState = {
  assignmentData: null,
  validationErrors: [],
  isDirty: false,
  saving: false,
  error: null,
}

// Async thunks
export const fetchAssignmentData = createAsyncThunk(
  'assignments/fetchAssignmentData',
  async (Id: string) => {
    const response = await assignmentService.getAssignmentData(Id)
    return response
  }
)

export const saveAssignmentData = createAsyncThunk(
  'assignments/saveAssignmentData',
  async ({ Id, assignmentData }: { Id: string; assignmentData: AssignmentConfiguration }) => {
    const response = await assignmentService.saveAssignmentData(Id, assignmentData)
    return response
  }
)

export const validateAssignmentData = createAsyncThunk(
  'assignments/validateAssignmentData',
  async (assignmentData: AssignmentConfiguration) => {
    const response = await assignmentService.validateAssignmentData(assignmentData)
    return response
  }
)

export const submitAssignmentData = createAsyncThunk(
  'assignments/submitAssignmentData',
  async (assignmentData: AssignmentConfiguration) => {
    const response = await assignmentService.submitAssignmentData(assignmentData)
    return response
  }
)

const assignmentsSlice = createSlice({
  name: 'assignments',
  initialState,
  reducers: {
    setAssignmentData: (state, action: PayloadAction<AssignmentConfiguration>) => {
      state.assignmentData = action.payload
      state.isDirty = true
    },
    updateMilestoneAssignment: (state, action: PayloadAction<{ milestoneId: string }>) => {
      // 不再需要更新里程碑负责人
    },
    addSubtask: (state, action: PayloadAction<{ milestoneId: string; subtask: SubtaskConfiguration }>) => {
      if (state.assignmentData && state.assignmentData.assignments) {
        const assignment = state.assignmentData.assignments.find(a => a.milestoneId === action.payload.milestoneId)
        if (assignment) {
          // Ensure subtasks array exists
          if (!assignment.subtasks) {
            assignment.subtasks = []
          }
          assignment.subtasks.push(action.payload.subtask)
          state.isDirty = true
        }
      }
    },
    removeSubtask: (state, action: PayloadAction<{ milestoneId: string; subtaskIndex: number }>) => {
      if (state.assignmentData && state.assignmentData.assignments) {
        const assignment = state.assignmentData.assignments.find(a => a.milestoneId === action.payload.milestoneId)
        if (assignment && assignment.subtasks && assignment.subtasks.length > action.payload.subtaskIndex) {
          assignment.subtasks.splice(action.payload.subtaskIndex, 1)
          state.isDirty = true
        }
      }
    },
    updateSubtask: (state, action: PayloadAction<{ milestoneId: string; subtaskIndex: number; subtask: Partial<SubtaskConfiguration> }>) => {
      if (state.assignmentData && state.assignmentData.assignments) {
        const assignment = state.assignmentData.assignments.find(a => a.milestoneId === action.payload.milestoneId)
        if (assignment && assignment.subtasks && assignment.subtasks.length > action.payload.subtaskIndex) {
          assignment.subtasks[action.payload.subtaskIndex] = {
            ...assignment.subtasks[action.payload.subtaskIndex],
            ...action.payload.subtask
          }
          state.isDirty = true
        }
      }
    },
    setValidationErrors: (state, action: PayloadAction<ValidationError[]>) => {
      state.validationErrors = action.payload
    },
    clearValidationErrors: (state) => {
      state.validationErrors = []
    },
    markClean: (state) => {
      state.isDirty = false
    },
    clearError: (state) => {
      state.error = null
    },
    resetAssignmentState: (state) => {
      state.assignmentData = null
      state.validationErrors = []
      state.isDirty = false
      state.saving = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch assignment data
      .addCase(fetchAssignmentData.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(fetchAssignmentData.fulfilled, (state, action) => {
        state.saving = false
        state.assignmentData = action.payload.data
        state.isDirty = false
      })
      .addCase(fetchAssignmentData.rejected, (state, action) => {
        state.saving = false
        state.error = action.error.message || 'Failed to fetch assignment data'
      })
      // Save assignment data
      .addCase(saveAssignmentData.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(saveAssignmentData.fulfilled, (state, action) => {
        state.saving = false
        state.assignmentData = action.payload.data
        state.isDirty = false
      })
      .addCase(saveAssignmentData.rejected, (state, action) => {
        state.saving = false
        state.error = action.error.message || 'Failed to save assignment data'
      })
      // Validate assignment data
      .addCase(validateAssignmentData.pending, (state) => {
        state.error = null
      })
      .addCase(validateAssignmentData.fulfilled, (state, action) => {
        state.validationErrors = action.payload.data.errors || []
      })
      .addCase(validateAssignmentData.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to validate assignment data'
      })
      // Submit assignment data
      .addCase(submitAssignmentData.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(submitAssignmentData.fulfilled, (state, action) => {
        state.saving = false
        state.assignmentData = action.payload.data
        state.isDirty = false
        state.validationErrors = []
      })
      .addCase(submitAssignmentData.rejected, (state, action) => {
        state.saving = false
        state.error = action.error.message || 'Failed to submit assignment data'
      })
  },
})

export const {
  setAssignmentData,
  updateMilestoneAssignment,
  addSubtask,
  removeSubtask,
  updateSubtask,
  setValidationErrors,
  clearValidationErrors,
  markClean,
  clearError,
  resetAssignmentState,
} = assignmentsSlice.actions

export default assignmentsSlice.reducer